import { Box, Link as ChakraLink, type BoxProps } from "@chakra-ui/react";
import { Icon } from "@equidefi/ui/icon";
import React from "react";
import { Link } from "react-router";

interface SectionButtonProps extends Omit<BoxProps, "onClick"> {
  children: React.ReactNode;
  to?: string;
  iconDirection?: "left" | "right";
  onClick?: () => void;
}

const SectionButton: React.FC<SectionButtonProps> = ({ children, to, iconDirection = "right", onClick, ...props }) => {
  const ariaLabel = typeof children === "string" ? children : "Navigate";
  const isLink = Boolean(to);

  const content = (
    <Box
      bg="white"
      borderRadius="md"
      boxShadow="md"
      p={4}
      mb={4}
      display="flex"
      justifyContent={iconDirection === "right" ? "space-between" : "flex-start"}
      alignItems="center"
      transition="all 0.2s"
      _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
      cursor="pointer"
      // Only add button role and keyboard handlers for non-link buttons
      {...(!isLink && {
        role: "button",
        "aria-label": ariaLabel,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
          }
        },
      })}
      {...props}>
      {iconDirection === "left" && (
        <Box flexShrink={0} mr={2}>
          <Icon.ChevronLeft size="1.5em" />
        </Box>
      )}
      <Box fontWeight="bold" fontSize="md" flex={1}>
        {children}
      </Box>
      {iconDirection === "right" && (
        <Box flexShrink={0}>
          <Icon.ChevronRight size="1.5em" />
        </Box>
      )}
    </Box>
  );

  if (isLink) {
    return (
      <ChakraLink as={Link} to={to} w="full" textDecoration="none" _hover={{ textDecoration: "none" }}>
        {content}
      </ChakraLink>
    );
  }

  return (
    <Box w="full" onClick={onClick}>
      {content}
    </Box>
  );
};

export default SectionButton;
